// CustomTable.tsx
import React, { useState } from "react";
import {
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Toolbar,
    Typography,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Switch,
    FormControlLabel,
    TextField,
    Box,
    Paper,
    Pagination,
    TablePagination,
    Chip,
} from "@mui/material";
import { MoreVertical, Eye, Edit, Trash2 } from "lucide-react";
import { formatTableDate, propertyType, statusColors, statusLabels } from "@/lib/utils";

type Order = "asc" | "desc";

type Action = {
    label: string;
    icon: React.ReactNode;
    onClick: (row: any) => void;
    color?: string
};

type CustomTableProps = {
    headers: { id: string; label: string }[];
    data: any[];
    count: number;
    page: number;
    limit: number;
    setPage?: (page: number) => void;
    setLimit?: (rows: number | any) => void;
    updateStatus?: any;
    actions?: Action[];
};

const CustomTable: React.FC<CustomTableProps> = ({
    headers,
    data,
    count,
    page,
    limit,
    setPage,
    setLimit,
    updateStatus,
    actions = [
        { label: "View", icon: <Eye size={16} />, onClick: (row) => console.log("View", row), color: "primary" },
        { label: "Edit", icon: <Edit size={16} />, onClick: (row) => console.log("Edit", row), color: "secondary" },
        { label: "Delete", icon: <Trash2 size={16} />, onClick: (row) => console.log("Delete", row), color: "destructive" },
    ],
}) => {
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<string>(headers[0]?.id || "");
    const [dense, setDense] = useState(false);
    const [filter, setFilter] = useState("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const filteredData = data?.filter((row) =>
        Object.values(row).some((val) =>
            String(val).toLowerCase().includes(filter.toLowerCase())
        )
    ) || [];

    const sortedData = [...filteredData]?.sort((a, b) => {
        if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
        return 0;
    });

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const getNestedValue = (obj: any, path: string) => {
        return path.split(".").reduce((acc, part) => acc && acc[part], obj);
    };

    const renderTableCell = (header: any, value: any, row: any) => {
        if (header.id == "specialties") return value?.map((specialty: any) => <Chip key={specialty} label={specialty} size="small" color="primary" />)
        if (header.id == "active") return <Tooltip title="Update Status" placement="top"><Chip label={value} sx={{ width: 100, cursor: "pointer" }} size="small" onClick={() => updateStatus(row)} color={value === "Active" ? "success" : "error"} /></Tooltip>
        if (header.id == "status") return <Chip label={statusLabels[value] || value} size="small" color={statusColors[value] || "error"} />
        if(header.id == "scheduledDate") return formatTableDate(value)
        if(header.id == "type") return <Chip label={propertyType[value] || value} size="small" color="primary" />
        return value
    };

    return (
        <Box sx={{ mt: 2 }}>
            <TableContainer>
                <Table size={dense ? "small" : "medium"}>
                    <TableHead sx={{ backgroundColor: "#F4F6F8" }}>
                        <TableRow>
                            {headers.map((header) => (
                                <TableCell key={header.id}>
                                    <TableSortLabel
                                        active={orderBy === header.id}
                                        direction={orderBy === header.id ? order : "asc"}
                                        onClick={() => handleRequestSort(header.id)}
                                    >
                                        {header.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            {actions.length > 0 && <TableCell align="right">Actions</TableCell>}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortedData.map((row, index) => (
                            <TableRow hover key={index}>
                                {headers.map((header) => {

                                    const value = getNestedValue(row, header.id);
                                    return (
                                        <TableCell key={header.id}>
                                            {
                                                renderTableCell(header, value, row)

                                            }

                                        </TableCell>)
                                })}
                                {actions.length > 0 && <TableCell align="right">
                                    <Tooltip title="Actions">
                                        <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                                            <MoreVertical size={18} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>}
                            </TableRow>
                        ))}
                        {
                            sortedData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={headers.length + (actions.length > 0 ? 1 : 0)}>
                                        <Typography variant="body2" color="error" fontWeight={700} fontSize={18} align="center">
                                            No data found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
                <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={dense}
                                onChange={(e) => setDense(e.target.checked)}
                            />
                        }
                        label="Dense"
                    />
                    <TablePagination
                        component="div"
                        count={count}
                        onPageChange={(e, page) => setPage && setPage(page)}
                        onRowsPerPageChange={(e: any) => setLimit && setLimit(e.target.value)}
                        page={page}
                        rowsPerPage={limit}
                    />
                </Box>
            </TableContainer>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "center",
                    horizontal: "left", // ✅ open to left side
                }}
                transformOrigin={{
                    vertical: "center",
                    horizontal: "right", // ✅ align correctly with arrow
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #f7d5d5 0%, #d5e0f4 100%)", // ✅ gradient
                        overflow: "visible",
                        ml: 1,
                        width: 150,
                        "&:before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: "50%",
                            right: 0,
                            width: 12,
                            height: 12,
                            bgcolor: "inherit", // match background
                            transform: "translateY(-50%) rotate(45deg)", // ✅ arrow shape
                            clipPath: "polygon(0 50%, 100% 0, 100% 100%)", // creates a ">"
                            zIndex: 0,
                        },
                    },
                }}
            >
                {actions.map((action) => (
                    <MenuItem
                        key={action.label}
                        onClick={() => {
                            action.onClick(selectedRow);
                            handleMenuClose();
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: action?.color || "black"
                        }}
                    >
                        {action.icon}
                        <Typography sx={{ ml: 1, fontWeight: 600 }}>{action.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>

        </Box>
    );
};

export default CustomTable;
